import { buildAuthUrl, generateCodeVerifier } from './pkce.js';
import { getClientId, getRedirectUri, getScopes } from './config.js';
import { saveTokens } from './tokenStore.js';

const STATE_KEY = 'ytcs:oauth-state';
const VERIFIER_KEY = 'ytcs:pkce-verifier';

export async function startOAuth() {
  const clientId = await getClientId();
  if (!clientId) {
    throw new Error('Missing OAuth client ID.');
  }

  const codeVerifier = generateCodeVerifier();
  const state = crypto.randomUUID();
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, codeVerifier);

  const url = await buildAuthUrl({
    clientId,
    redirectUri: getRedirectUri(),
    scope: getScopes(),
    state,
    codeVerifier
  });

  window.location.assign(url);
}

export async function exchangeCodeForToken({ code, vaultKey }) {
  const clientId = await getClientId();
  const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
  const state = sessionStorage.getItem(STATE_KEY);

  if (!clientId) {
    throw new Error('Missing OAuth client ID.');
  }

  if (!codeVerifier) {
    throw new Error('Missing code verifier. Start OAuth again.');
  }

  const body = new URLSearchParams({
    client_id: clientId,
    code,
    code_verifier: codeVerifier,
    redirect_uri: getRedirectUri(),
    grant_type: 'authorization_code'
  });

  const response = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error_description || payload.error || 'Token exchange failed.');
  }

  const tokenData = await response.json();
  const stored = {
    ...tokenData,
    obtained_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + tokenData.expires_in * 1000).toISOString(),
    state
  };

  await saveTokens(vaultKey, stored);
  return stored;
}

export async function refreshAccessToken({ refreshToken, vaultKey }) {
  const clientId = await getClientId();
  if (!clientId) {
    throw new Error('Missing OAuth client ID.');
  }
  if (!refreshToken) {
    throw new Error('Missing refresh token.');
  }

  const body = new URLSearchParams({
    client_id: clientId,
    refresh_token: refreshToken,
    grant_type: 'refresh_token'
  });

  const response = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error_description || payload.error || 'Token refresh failed.');
  }

  const tokenData = await response.json();
  const stored = {
    ...tokenData,
    refresh_token: refreshToken,
    obtained_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + tokenData.expires_in * 1000).toISOString()
  };

  await saveTokens(vaultKey, stored);
  return stored;
}
